import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing auth data on mount
  useEffect(() => {
    const authData = localStorage.getItem('sf_auth');
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        // Format user data properly
        setUser({
          name: parsed.user_name,
          email: parsed.user_email,
          orgId: parsed.org_id,
          instanceUrl: parsed.instance_url
        });
        setIsAuthenticated(true);
        // Set default headers for all requests
        setAxiosDefaults(parsed);
      } catch (error) {
        console.error('Failed to parse auth data:', error);
        localStorage.removeItem('sf_auth');
      }
    }
    setLoading(false);
  }, []);

  const setAxiosDefaults = (authData) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${authData.access_token}`;
    axios.defaults.headers.common['X-Instance-Url'] = authData.instance_url;
    axios.defaults.headers.common['X-User-Name'] = authData.user_name;
    axios.defaults.headers.common['X-User-Email'] = authData.user_email;
    axios.defaults.headers.common['X-Org-Id'] = authData.org_id;
  };

  const setAuth = (authData) => {
    // Store auth data from OAuth callback
    localStorage.setItem('sf_auth', JSON.stringify(authData));
    setAxiosDefaults(authData);
    
    setIsAuthenticated(true);
    setUser({
      name: authData.user_name,
      email: authData.user_email,
      orgId: authData.org_id,
      instanceUrl: authData.instance_url
    });
  };

  const logout = () => {
    localStorage.removeItem('sf_auth');
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Instance-Url'];
    delete axios.defaults.headers.common['X-User-Name'];
    delete axios.defaults.headers.common['X-User-Email'];
    delete axios.defaults.headers.common['X-Org-Id'];
    setIsAuthenticated(false);
    setUser(null);
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    setAuth,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
